// AdminChewPunchlist — recurring walk-through inspection sheet for Chew Flats.
// Left rail: list of walks (dated inspections). Main pane: items grouped by
// unit with per-item status/priority/category, per-item "Create WO" button,
// bulk multi-select actions, filter, progress bar, and a "Run AI classify"
// button that refines all items with Claude Sonnet.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminShell as Layout } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, ClipboardText, Plus, Wrench, CheckCircle,
         Circle, ArrowRight, Trash, X, Sparkle, Warning } from "@phosphor-icons/react";

const CAT_COLOR = {
  cleaning: "bg-cyan-50 text-cyan-700 border-cyan-200",
  appliance: "bg-orange-50 text-orange-700 border-orange-200",
  structural: "bg-amber-50 text-amber-700 border-amber-200",
  electrical: "bg-yellow-50 text-yellow-800 border-yellow-200",
  hvac: "bg-sky-50 text-sky-700 border-sky-200",
  pest: "bg-red-50 text-red-700 border-red-200",
  plumbing: "bg-blue-50 text-blue-700 border-blue-200",
  furniture: "bg-purple-50 text-purple-700 border-purple-200",
  other: "bg-slate-50 text-slate-600 border-slate-200",
};
const PRIO_STYLE = {
  urgent: "bg-red-500 text-white",
  high:   "bg-orange-500 text-white",
  normal: "bg-slate-200 text-slate-700",
};
const STATUS_ICON = {
  open: <Circle size={14} className="text-slate-400"/>,
  in_progress: <Wrench weight="fill" size={14} className="text-orange-500"/>,
  done: <CheckCircle weight="fill" size={14} className="text-emerald-500"/>,
  wont_fix: <X size={14} className="text-slate-500"/>,
};
const CATEGORIES = ["cleaning","appliance","structural","electrical","hvac","pest","plumbing","furniture","other"];
const PRIORITIES = ["normal","high","urgent"];
const STATUSES = ["open","in_progress","done","wont_fix"];

export default function AdminChewPunchlist() {
  const nav = useNavigate();
  const [walks, setWalks] = useState([]);
  const [activeWalkId, setActiveWalkId] = useState(null);
  const [walk, setWalk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("open");
  const [catFilter, setCatFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showNewWalk, setShowNewWalk] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  async function loadWalks() {
    setLoading(true);
    try {
      const r = await api.get("/chew/punchlist/walks");
      setWalks(r.data.walks || []);
      if (!activeWalkId && r.data.walks?.length) setActiveWalkId(r.data.walks[0].id);
    } catch (e) { toast.error("Failed to load walks"); }
    finally { setLoading(false); }
  }
  async function loadWalk(id) {
    if (!id) return;
    try {
      const r = await api.get(`/chew/punchlist/walks/${id}`);
      setWalk(r.data.walk);
      setSelectedIds(new Set());
    } catch (e) { toast.error("Failed to load walk"); }
  }

  useEffect(() => { loadWalks(); }, []);
  useEffect(() => { if (activeWalkId) loadWalk(activeWalkId); }, [activeWalkId]);

  const items = useMemo(() => {
    if (!walk) return [];
    return (walk.items || []).filter(i => {
      if (statusFilter && i.status !== statusFilter) return false;
      if (catFilter && i.category !== catFilter) return false;
      if (unitFilter && i.unit !== unitFilter) return false;
      return true;
    });
  }, [walk, statusFilter, catFilter, unitFilter]);

  const grouped = useMemo(() => {
    const g = {};
    items.forEach(i => { (g[i.unit] = g[i.unit] || []).push(i); });
    return Object.entries(g).sort(([a],[b]) => a.localeCompare(b, "en", { numeric: true }));
  }, [items]);

  const allUnits = useMemo(() => walk ? [...new Set((walk.items||[]).map(i=>i.unit))].sort((a,b)=>a.localeCompare(b,"en",{numeric:true})) : [], [walk]);

  function toggleSelect(id) {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  }
  function selectAllVisible() {
    setSelectedIds(new Set(items.filter(i => !i.wo_id).map(i => i.id)));
  }

  async function updateItem(itemId, patch) {
    try {
      await api.patch(`/chew/punchlist/walks/${activeWalkId}/items/${itemId}`, patch);
      await loadWalk(activeWalkId); await loadWalks();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Update failed")); }
  }
  async function deleteItem(itemId) {
    if (!confirm("Delete this punchlist item?")) return;
    try {
      await api.delete(`/chew/punchlist/walks/${activeWalkId}/items/${itemId}`);
      await loadWalk(activeWalkId); await loadWalks();
    } catch (e) { toast.error("Delete failed"); }
  }
  async function createWo(itemId) {
    try {
      const r = await api.post(`/chew/punchlist/walks/${activeWalkId}/items/${itemId}/create-wo`);
      toast.success(r.data.already ? "WO already exists" : "Work order created");
      await loadWalk(activeWalkId); await loadWalks();
    } catch (e) { toast.error("WO create failed"); }
  }
  async function bulkCreateWos() {
    if (!selectedIds.size) return;
    if (!confirm(`Create ${selectedIds.size} work orders? This will notify LaSalle/ProCall/Owner.`)) return;
    try {
      const r = await api.post(`/chew/punchlist/walks/${activeWalkId}/create-wos-bulk`,
        { item_ids: [...selectedIds] });
      toast.success(`${r.data.created_count} WOs created${r.data.skipped?.length ? `, ${r.data.skipped.length} skipped` : ""}`);
      setSelectedIds(new Set());
      await loadWalk(activeWalkId); await loadWalks();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Bulk WO create failed")); }
  }
  async function bulkStatus(status) {
    if (!selectedIds.size) return;
    try {
      await api.post(`/chew/punchlist/walks/${activeWalkId}/items/bulk-status`,
        { item_ids: [...selectedIds], status });
      toast.success(`Marked ${selectedIds.size} as ${status.replace("_"," ")}`);
      setSelectedIds(new Set());
      await loadWalk(activeWalkId); await loadWalks();
    } catch (e) { toast.error("Bulk status failed"); }
  }
  async function runAi() {
    setAiBusy(true);
    try {
      const r = await api.post(`/chew/punchlist/walks/${activeWalkId}/ai-classify`);
      toast.success(r.data.classified ? `AI reclassified ${r.data.classified} items` : "Nothing to classify");
      await loadWalk(activeWalkId);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI classify failed")); }
    finally { setAiBusy(false); }
  }

  async function seedLasalle() {
    try {
      const r = await api.post("/chew/punchlist/seed/lasalle-2026-08-18");
      if (r.data.already_existed) {
        toast.success(`LaSalle 8/18/26 walk already loaded (${r.data.item_count} items)`);
      } else {
        toast.success(`Loaded LaSalle 8/18/26 walk — ${r.data.item_count} items`);
      }
      await loadWalks();
      setActiveWalkId(r.data.walk_id);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Import failed")); }
  }

  const progress = walk?.stats ? Math.round(((walk.stats.done + walk.stats.wont_fix) / (walk.stats.total || 1)) * 100) : 0;

  return (
    <Layout>
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="admin-chew-punchlist">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-500">Chew Flats · Walk Punchlists</div>
            <h1 className="text-2xl font-black mt-1 flex items-center gap-2"><ClipboardText weight="fill" className="text-cyan-500"/> Walk Punchlist</h1>
            <p className="text-sm text-slate-500 mt-1">Every LaSalle walk-through as a tracked list. Fire WOs from items you actually want fixed.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={seedLasalle} className="bg-[#0a2461] text-white rounded-xl font-black px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-[#123a9a]" data-testid="seed-lasalle-btn" title="One-click import of the LaSalle 8/18/26 punchlist doc — safe to click, won't duplicate">
              <ClipboardText weight="fill" size={16}/> Load LaSalle 8/18/26 walk
            </button>
            <button onClick={()=>setShowNewWalk(true)} className="bg-slate-900 text-white rounded-xl font-black px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-slate-800" data-testid="new-walk-btn"><Plus size={16}/> New walk</button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
          {/* Walk list */}
          <aside className="space-y-2 lg:sticky lg:top-6 self-start">
            {loading && <div className="text-xs text-slate-400">Loading…</div>}
            {walks.map(w => (
              <button key={w.id} onClick={()=>setActiveWalkId(w.id)}
                className={`w-full text-left rounded-xl p-3 border-2 transition ${activeWalkId===w.id ? "border-cyan-500 bg-cyan-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                data-testid={`walk-tab-${w.id}`}>
                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500"><Calendar size={12}/> {w.display_label || w.walked_on}</div>
                <div className="font-black text-sm mt-1 truncate">{w.walked_by}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{w.stats.total} items · {w.stats.done + w.stats.wont_fix}/{w.stats.total} closed</div>
                <div className="mt-1.5 bg-slate-200 h-1 rounded-full overflow-hidden">
                  <div className="bg-emerald-500 h-full" style={{width: `${Math.round(((w.stats.done + w.stats.wont_fix) / (w.stats.total || 1)) * 100)}%`}}/>
                </div>
                {w.stats.urgent > 0 && (
                  <div className="mt-1.5 inline-flex items-center gap-1 bg-red-500 text-white text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full"><Warning size={10}/> {w.stats.urgent} urgent</div>
                )}
              </button>
            ))}
            {!walks.length && !loading && <div className="text-sm text-slate-400 italic">No walks yet. Start one above.</div>}
          </aside>

          {/* Walk detail */}
          <div className="min-w-0">
            {!walk && !loading && <div className="bg-slate-50 rounded-2xl p-10 text-center text-slate-500">Pick a walk on the left.</div>}
            {walk && (
              <>
                {/* Progress banner */}
                <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{walk.display_label || walk.walked_on} · {walk.walked_by}</div>
                      {walk.note && <div className="text-xs text-slate-500 mt-0.5">{walk.note}</div>}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={runAi} disabled={aiBusy} className="text-xs font-bold bg-fuchsia-100 hover:bg-fuchsia-200 text-fuchsia-800 rounded-lg px-3 py-1.5 flex items-center gap-1 disabled:opacity-50" data-testid="ai-classify-btn"><Sparkle weight="fill" size={12}/>{aiBusy ? "Classifying…" : "Run AI classify"}</button>
                      <button onClick={()=>setShowQuickAdd(true)} className="text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg px-3 py-1.5 flex items-center gap-1" data-testid="quick-add-btn"><Plus size={12}/> Add item</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <MiniStat n={walk.stats.total} label="Total"/>
                    <MiniStat n={walk.stats.open} label="Open" color="text-slate-700"/>
                    <MiniStat n={walk.stats.in_progress} label="WO placed" color="text-orange-600"/>
                    <MiniStat n={walk.stats.done + walk.stats.wont_fix} label="Closed" color="text-emerald-600"/>
                  </div>
                  <div className="mt-3 bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-500 to-cyan-500 h-full transition-all" style={{width: `${progress}%`}}/>
                  </div>
                  <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400 mt-1">{progress}% closed</div>
                </div>

                {/* Filters + bulk bar */}
                <div className="flex items-center flex-wrap gap-2 mb-3">
                  {STATUSES.concat([""]).map(s => (
                    <button key={s||"all"} onClick={()=>setStatusFilter(s)}
                      className={`text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full ${statusFilter===s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                      data-testid={`filter-status-${s||"all"}`}>
                      {s ? s.replace("_"," ") : "All"}
                    </button>
                  ))}
                  <select value={catFilter} onChange={e=>setCatFilter(e.target.value)} className="text-xs bg-white border border-slate-300 rounded-lg px-2 py-1.5" data-testid="filter-cat">
                    <option value="">All categories</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select value={unitFilter} onChange={e=>setUnitFilter(e.target.value)} className="text-xs bg-white border border-slate-300 rounded-lg px-2 py-1.5" data-testid="filter-unit">
                    <option value="">All units</option>
                    {allUnits.map(u => <option key={u} value={u}>Unit {u}</option>)}
                  </select>
                  <div className="ml-auto text-xs text-slate-500 font-bold">{items.length} shown</div>
                </div>

                {selectedIds.size > 0 && (
                  <div className="sticky top-4 z-10 bg-slate-900 text-white rounded-xl p-3 mb-3 flex items-center gap-2 flex-wrap shadow-lg" data-testid="bulk-bar">
                    <span className="text-sm font-black">{selectedIds.size} selected</span>
                    <div className="ml-auto flex gap-2 flex-wrap">
                      <button onClick={bulkCreateWos} className="text-xs font-black bg-emerald-500 hover:bg-emerald-600 rounded-lg px-3 py-1.5 flex items-center gap-1" data-testid="bulk-create-wos"><Wrench weight="fill" size={12}/> Create {selectedIds.size} WOs</button>
                      <button onClick={()=>bulkStatus("done")} className="text-xs font-black bg-slate-700 hover:bg-slate-600 rounded-lg px-3 py-1.5" data-testid="bulk-mark-done">Mark done</button>
                      <button onClick={()=>bulkStatus("wont_fix")} className="text-xs font-black bg-slate-700 hover:bg-slate-600 rounded-lg px-3 py-1.5" data-testid="bulk-mark-wontfix">Won't fix</button>
                      <button onClick={()=>setSelectedIds(new Set())} className="text-xs font-black text-slate-300 hover:text-white" data-testid="bulk-clear">Clear</button>
                    </div>
                  </div>
                )}

                {selectedIds.size === 0 && items.length > 0 && (
                  <button onClick={selectAllVisible} className="text-[11px] font-bold text-cyan-600 hover:underline mb-2" data-testid="select-all-btn">Select all visible ({items.filter(i=>!i.wo_id).length} without WOs)</button>
                )}

                {/* Item groups */}
                <div className="space-y-4">
                  {grouped.map(([unit, unitItems]) => (
                    <div key={unit} className="bg-white rounded-xl shadow-sm overflow-hidden">
                      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                        <div className="font-black text-sm">Unit {unit}</div>
                        <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">{unitItems.length} item{unitItems.length===1?"":"s"}</div>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {unitItems.map(it => (
                          <ItemRow key={it.id} item={it}
                            selected={selectedIds.has(it.id)}
                            onToggle={()=>toggleSelect(it.id)}
                            onUpdate={patch=>updateItem(it.id, patch)}
                            onDelete={()=>deleteItem(it.id)}
                            onCreateWo={()=>createWo(it.id)}
                            onOpenWo={(id)=>nav(`/admin/jobs/${id}`)}/>
                        ))}
                      </div>
                    </div>
                  ))}
                  {!grouped.length && (
                    <div className="bg-slate-50 rounded-xl p-8 text-center text-slate-500 text-sm">Nothing matches the current filters.</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showNewWalk && <NewWalkModal onClose={()=>setShowNewWalk(false)} onCreated={id=>{setActiveWalkId(id); loadWalks();}}/>}
      {showQuickAdd && walk && <QuickAddModal walkId={activeWalkId} allUnits={allUnits} onClose={()=>setShowQuickAdd(false)} onAdded={()=>{loadWalk(activeWalkId); loadWalks();}}/>}
    </Layout>
  );
}

function MiniStat({ n, label, color = "text-slate-900" }) {
  return (
    <div className="bg-slate-50 rounded-lg py-2">
      <div className={`text-xl font-black ${color}`}>{n}</div>
      <div className="text-[9px] uppercase font-bold tracking-widest text-slate-500">{label}</div>
    </div>
  );
}

function ItemRow({ item, selected, onToggle, onUpdate, onDelete, onCreateWo, onOpenWo }) {
  const catStyle = CAT_COLOR[item.category] || CAT_COLOR.other;
  const prioStyle = PRIO_STYLE[item.priority] || PRIO_STYLE.normal;
  const hasWo = !!item.wo_id;

  return (
    <div className={`p-3 flex items-start gap-3 ${selected ? "bg-cyan-50/40" : "hover:bg-slate-50"}`} data-testid={`punch-row-${item.id}`}>
      <input type="checkbox" checked={selected} onChange={onToggle} disabled={hasWo}
             className="mt-1.5 w-4 h-4 accent-cyan-500 disabled:opacity-30" data-testid={`punch-select-${item.id}`}/>
      <div className="flex-shrink-0 pt-1.5">{STATUS_ICON[item.status]}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 flex-wrap">
          {item.room && <span className="text-[11px] font-black text-slate-700 uppercase tracking-widest">{item.room}</span>}
          <span className="text-sm text-slate-900 flex-1 min-w-[200px]">{item.issue}</span>
        </div>
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          <select value={item.category} onChange={e=>onUpdate({category:e.target.value})}
            className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-md border ${catStyle}`}
            data-testid={`punch-cat-${item.id}`}>
            {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <select value={item.priority} onChange={e=>onUpdate({priority:e.target.value})}
            className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-md border-0 ${prioStyle}`}
            data-testid={`punch-prio-${item.id}`}>
            {PRIORITIES.map(p=><option key={p} value={p}>{p}</option>)}
          </select>
          <select value={item.status} onChange={e=>onUpdate({status:e.target.value})}
            className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700 border-0"
            data-testid={`punch-status-${item.id}`}>
            {STATUSES.map(s=><option key={s} value={s}>{s.replace("_"," ")}</option>)}
          </select>
          {item.ai_classified && <span className="text-[9px] font-black uppercase tracking-widest text-fuchsia-500 flex items-center gap-0.5"><Sparkle weight="fill" size={9}/> AI</span>}
        </div>
      </div>
      <div className="flex flex-col gap-1 items-end">
        {hasWo ? (
          <button onClick={()=>onOpenWo(item.wo_id)} className="text-[10px] font-black bg-emerald-100 text-emerald-800 rounded-lg px-2 py-1 flex items-center gap-1 hover:bg-emerald-200" data-testid={`punch-open-wo-${item.id}`}>WO placed <ArrowRight size={10}/></button>
        ) : (
          <button onClick={onCreateWo} className="text-[10px] font-black bg-slate-900 text-white rounded-lg px-2 py-1 hover:bg-slate-800" data-testid={`punch-create-wo-${item.id}`}>Create WO</button>
        )}
        <button onClick={onDelete} className="text-slate-300 hover:text-red-500" title="Delete item" data-testid={`punch-delete-${item.id}`}><Trash size={14}/></button>
      </div>
    </div>
  );
}

function NewWalkModal({ onClose, onCreated }) {
  const [walkedOn, setWalkedOn] = useState((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })());
  const [walkedBy, setWalkedBy] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  async function create() {
    if (!walkedBy.trim()) { toast.error("Who walked it?"); return; }
    setBusy(true);
    try {
      const r = await api.post("/chew/punchlist/walks", {
        walked_on: walkedOn, walked_by: walkedBy, note,
        source_doc: "",
      });
      toast.success("Walk created");
      onCreated(r.data.walk.id);
      onClose();
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e=>e.stopPropagation()} data-testid="new-walk-modal">
        <h3 className="text-lg font-black mb-4">Start a new walk-through</h3>
        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Date walked</div>
            <input type="date" value={walkedOn} onChange={e=>setWalkedOn(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2" data-testid="new-walk-date"/>
          </div>
          <div>
            <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Walked by</div>
            <input value={walkedBy} onChange={e=>setWalkedBy(e.target.value)} placeholder="e.g. LaSalle Housing / Andrew" className="w-full border border-slate-300 rounded-lg px-3 py-2" data-testid="new-walk-by"/>
          </div>
          <div>
            <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Note</div>
            <textarea value={note} onChange={e=>setNote(e.target.value)} rows={2} placeholder="Turnover / mid-lease / punchlist purpose" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="new-walk-note"/>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={onClose} className="flex-1 bg-slate-100 rounded-lg py-2.5 font-bold text-sm">Cancel</button>
            <button onClick={create} disabled={busy} className="flex-1 bg-slate-900 text-white rounded-lg py-2.5 font-black text-sm disabled:opacity-50" data-testid="new-walk-create">{busy ? "Creating…" : "Create walk"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickAddModal({ walkId, allUnits, onClose, onAdded }) {
  const [unit, setUnit] = useState(allUnits[0] || "");
  const [room, setRoom] = useState("");
  const [issue, setIssue] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit() {
    if (!unit || !issue.trim()) { toast.error("Unit + issue required"); return; }
    setBusy(true);
    try {
      await api.post(`/chew/punchlist/walks/${walkId}/items`, { unit, room, issue });
      toast.success("Item added");
      onAdded(); onClose();
    } catch (e) { toast.error("Add failed"); }
    finally { setBusy(false); }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e=>e.stopPropagation()} data-testid="quick-add-modal">
        <h3 className="text-lg font-black mb-4">Add punchlist item</h3>
        <div className="space-y-3">
          <div>
            <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Unit</div>
            <input list="units-list" value={unit} onChange={e=>setUnit(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 uppercase" placeholder="e.g. 8B" data-testid="qa-unit"/>
            <datalist id="units-list">{allUnits.map(u => <option key={u} value={u}/>)}</datalist>
          </div>
          <div>
            <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Room</div>
            <input value={room} onChange={e=>setRoom(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2" placeholder="Kitchen / Bathroom / …" data-testid="qa-room"/>
          </div>
          <div>
            <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Issue</div>
            <textarea value={issue} onChange={e=>setIssue(e.target.value)} rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="What needs attention?" data-testid="qa-issue"/>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={onClose} className="flex-1 bg-slate-100 rounded-lg py-2.5 font-bold text-sm">Cancel</button>
            <button onClick={submit} disabled={busy} className="flex-1 bg-slate-900 text-white rounded-lg py-2.5 font-black text-sm disabled:opacity-50" data-testid="qa-submit">{busy ? "Adding…" : "Add"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
